import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Download, Trash2, Expand, X, Loader2, ChevronLeft, ChevronRight, ExternalLink, Sparkles, ImagePlus,
} from "lucide-react";

/**
 * "My Cards" — a personal wallet of tap-and-hand QR codes.
 *
 * Card slot 1 is always the user's TBC referral QR (auto-fetched from
 * /api/referrals/me → /join?ref=TBC-XXXXXX). Additional cards are stored in
 * `localStorage["tbc.my_cards"]` so admins can add their real-estate card,
 * Repkilla, MTG portfolio, landlord-review site, etc. without needing a
 * backend table.
 *
 * The whole point is fast in-person sharing: tap a card, phone fills with a
 * huge white-on-navy QR, hand it over, done.
 */

const STORAGE_KEY = "tbc.my_cards";

function loadCards() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

function saveCards(cards) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cards));
  } catch { /* localStorage may be blocked */ }
}

export default function MyCardsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customCards, setCustomCards] = useState(() => loadCards());
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ title: "", subtitle: "", url: "", logo: "" });
  const [fullscreenCard, setFullscreenCard] = useState(null);

  // Redirect to sign-in if unauthed once auth finishes resolving
  useEffect(() => {
    if (user === null) {
      toast.error("Sign in to view your cards.");
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/referrals/me`, { withCredentials: true })
      .then((r) => { if (!cancelled) setReferral(r.data); })
      .catch(() => { if (!cancelled) setReferral(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const addCard = () => {
    const title = addForm.title.trim();
    let url = addForm.url.trim();
    if (!title || !url) {
      toast.error("Title and URL are required.");
      return;
    }
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    const next = [
      ...customCards,
      {
        id: `card_${Date.now()}`,
        title,
        subtitle: addForm.subtitle.trim(),
        url,
        logo: addForm.logo || "",
      },
    ];
    setCustomCards(next);
    saveCards(next);
    setAddOpen(false);
    setAddForm({ title: "", subtitle: "", url: "", logo: "" });
    toast.success(`"${title}" added to your cards.`);
  };

  const removeCard = (id) => {
    if (!window.confirm("Remove this card?")) return;
    const next = customCards.filter((c) => c.id !== id);
    setCustomCards(next);
    saveCards(next);
  };

  const tbcCard = referral?.share_url
    ? {
        id: "tbc-referral",
        title: "The Blk Community",
        subtitle: `Refer a business — earn 50 pts each · code ${referral.code || ""}`,
        url: referral.share_url,
        highlight: true,
      }
    : null;

  const allCards = [tbcCard, ...customCards].filter(Boolean);

  return (
    <div className="min-h-screen bg-tbc-cream/40">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-gray-600 hover:text-tbc-navy text-sm mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <header className="mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl text-tbc-navy">My Cards</h1>
          <p className="text-sm text-gray-600 mt-1 max-w-xl">
            Your personal wallet of scannable QR codes. Tap any card to go
            full-screen, then hand your phone over so someone can scan it in
            person. Add cards for every link you share often.
          </p>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your cards…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cards-grid">
              {allCards.map((card) => (
                <CardTile
                  key={card.id}
                  card={card}
                  onOpen={() => setFullscreenCard(card)}
                  onRemove={card.highlight ? null : () => removeCard(card.id)}
                />
              ))}

              {/* Add-card tile */}
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <button
                    className="rounded-2xl border-2 border-dashed border-tbc-navy/30 bg-white/50 hover:bg-white hover:border-tbc-navy transition-colors min-h-[300px] flex flex-col items-center justify-center text-tbc-navy p-6"
                    data-testid="add-card-btn"
                  >
                    <Plus className="w-8 h-8 mb-2" />
                    <span className="font-heading text-lg">Add a card</span>
                    <span className="text-xs text-gray-500 mt-1 text-center">
                      Real estate, Repkilla, Midas Touch — any link you share.
                    </span>
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-tbc-navy">
                      <Sparkles className="w-5 h-5 text-tbc-gold" />
                      Add a new card
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <div>
                      <Label htmlFor="card-title" className="text-sm">Title</Label>
                      <Input
                        id="card-title"
                        value={addForm.title}
                        onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                        placeholder="e.g. Matt Walker — Real Estate"
                        data-testid="card-title-input"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="card-subtitle" className="text-sm">Subtitle (optional)</Label>
                      <Input
                        id="card-subtitle"
                        value={addForm.subtitle}
                        onChange={(e) => setAddForm({ ...addForm, subtitle: e.target.value })}
                        placeholder="e.g. Licensed agent · DMV area"
                        data-testid="card-subtitle-input"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="card-url" className="text-sm">URL</Label>
                      <Input
                        id="card-url"
                        value={addForm.url}
                        onChange={(e) => setAddForm({ ...addForm, url: e.target.value })}
                        placeholder="https://linktr.ee/kingmidas"
                        data-testid="card-url-input"
                        className="mt-1"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Cards are stored on this device only. If you switch phones you&apos;ll re-add them here.
                      </p>
                    </div>
                    {/* Optional logo — shown above the QR so scanners see the brand
                        before they scan. Stored as base64 data-url in localStorage. */}
                    <div>
                      <Label className="text-sm">Logo (optional)</Label>
                      <div className="mt-1 flex items-center gap-3">
                        {addForm.logo ? (
                          <div className="relative">
                            <img
                              src={addForm.logo}
                              alt="Logo preview"
                              className="w-14 h-14 rounded-lg object-cover border border-gray-200"
                              data-testid="card-logo-preview"
                            />
                            <button
                              type="button"
                              onClick={() => setAddForm({ ...addForm, logo: "" })}
                              className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 hover:bg-red-700"
                              aria-label="Remove logo"
                              data-testid="card-logo-remove-btn"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-14 h-14 rounded-lg border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                            <ImagePlus className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <label className="cursor-pointer text-sm text-tbc-navy hover:underline">
                          <span data-testid="card-logo-picker">
                            {addForm.logo ? "Replace logo" : "Choose a logo"}
                          </span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 300 * 1024) {
                                toast.error("Logo too big — 300 KB max. Try a smaller file.");
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = () => setAddForm({ ...addForm, logo: reader.result });
                              reader.readAsDataURL(file);
                            }}
                          />
                        </label>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">
                        Square works best. 300 KB max — kept on your device.
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="card-cancel-btn">
                      Cancel
                    </Button>
                    <Button
                      onClick={addCard}
                      className="bg-tbc-navy text-white hover:bg-tbc-navy/90"
                      data-testid="card-save-btn"
                    >
                      Add card
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {allCards.length === 1 && (
              <p className="text-center text-sm text-gray-500 mt-6">
                💡 Save this page to your home screen for one-tap access at events.
              </p>
            )}
          </>
        )}
      </main>

      <Footer />

      {fullscreenCard && (
        <FullscreenCard
          card={fullscreenCard}
          cards={allCards}
          onClose={() => setFullscreenCard(null)}
          onSwitch={(next) => setFullscreenCard(next)}
        />
      )}
    </div>
  );
}

function CardTile({ card, onOpen, onRemove }) {
  return (
    <div
      className={`relative rounded-2xl border overflow-hidden shadow-sm transition-transform hover:-translate-y-0.5 ${
        card.highlight
          ? "border-tbc-gold/50 bg-tbc-navy text-white"
          : "border-gray-200 bg-white text-tbc-navy"
      }`}
      data-testid={`card-tile-${card.id}`}
    >
      <button
        onClick={onOpen}
        className="w-full text-left p-5 flex flex-col items-center gap-3"
        data-testid={`card-open-${card.id}`}
      >
        {card.logo && (
          <img
            src={card.logo}
            alt=""
            className="w-14 h-14 rounded-full object-cover border-2 border-tbc-gold/40 -mb-1"
            data-testid={`card-logo-${card.id}`}
          />
        )}
        <div className={`p-3 rounded-xl ${card.highlight ? "bg-white" : "bg-tbc-cream/40"}`}>
          <QRCodeCanvas
            value={card.url}
            size={160}
            level="M"
            includeMargin={false}
            fgColor="#0A192F"
            bgColor="#ffffff"
          />
        </div>
        <div className="text-center">
          <p className={`font-heading text-lg ${card.highlight ? "text-tbc-gold" : "text-tbc-navy"}`}>
            {card.title}
          </p>
          {card.subtitle && (
            <p className={`text-xs mt-1 ${card.highlight ? "text-white/70" : "text-gray-500"}`}>
              {card.subtitle}
            </p>
          )}
        </div>
        <div className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider mt-1 ${
          card.highlight ? "text-white/70" : "text-gray-400"
        }`}>
          <Expand className="w-3 h-3" /> Tap to present
        </div>
      </button>
      {onRemove && (
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
          aria-label="Remove card"
          data-testid={`card-remove-${card.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function FullscreenCard({ card, cards, onClose, onSwitch }) {
  const canvasWrapRef = useRef(null);
  const idx = cards.findIndex((c) => c.id === card.id);
  const prev = idx > 0 ? cards[idx - 1] : null;
  const next = idx < cards.length - 1 ? cards[idx + 1] : null;

  // Escape / arrow-key nav on desktop
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && prev) onSwitch(prev);
      if (e.key === "ArrowRight" && next) onSwitch(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onClose, onSwitch]);

  const download = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const safe = card.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${safe}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-tbc-navy flex flex-col items-center justify-center px-6 py-10"
      data-testid="fullscreen-card"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        aria-label="Close"
        data-testid="fullscreen-close"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="w-full max-w-md flex flex-col items-center gap-6">
        <div className="text-center text-white">
          {card.logo && (
            <img
              src={card.logo}
              alt=""
              className="w-20 h-20 rounded-full object-cover border-2 border-tbc-gold mx-auto mb-3"
              data-testid="fullscreen-logo"
            />
          )}
          <p className="font-heading text-2xl sm:text-3xl text-tbc-gold">{card.title}</p>
          {card.subtitle && (
            <p className="text-sm text-white/70 mt-1 max-w-sm mx-auto">{card.subtitle}</p>
          )}
        </div>

        <div
          ref={canvasWrapRef}
          className="p-6 bg-white rounded-3xl shadow-2xl"
          data-testid="fullscreen-qr"
        >
          <QRCodeCanvas
            value={card.url}
            size={320}
            level="H"
            includeMargin={false}
            fgColor="#0A192F"
            bgColor="#ffffff"
          />
        </div>

        <p className="text-center text-xs text-white/60 font-mono break-all max-w-xs">
          {card.url}
        </p>

        <div className="flex flex-wrap justify-center gap-3">
          <Button
            onClick={download}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            data-testid="fullscreen-download"
          >
            <Download className="w-4 h-4 mr-2" /> Save PNG
          </Button>
          <a
            href={card.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm border border-white/20 bg-white/10 text-white hover:bg-white/20"
            data-testid="fullscreen-open"
          >
            <ExternalLink className="w-4 h-4 mr-2" /> Open link
          </a>
        </div>
      </div>

      {/* Prev/Next arrows for switching between cards without exiting */}
      {prev && (
        <button
          onClick={() => onSwitch(prev)}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Previous card"
          data-testid="fullscreen-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {next && (
        <button
          onClick={() => onSwitch(next)}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Next card"
          data-testid="fullscreen-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {cards.length > 1 && (
        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-1.5">
          {cards.map((c, i) => (
            <span
              key={c.id}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-tbc-gold" : "w-1.5 bg-white/30"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
